import React, { Component } from 'react';
import _ from 'underscore';
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@cargomatic/ui';
import { Info } from 'lucide-react';
import { getSortedReasonCodes } from 'utils/ReasonCodesAPI';
import { withTranslation } from 'react-i18next';
import { addlocalizationSlugKey } from 'utils/localization/utils';

function InfoTooltip({ children }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="ml-1 inline-flex items-center text-muted-foreground">
            <Info className="size-4" />
          </span>
        </TooltipTrigger>
        <TooltipContent>{children}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

class SelectReasonCodes extends Component {
  constructor(props) {
    super(props);
    this.state = {
      subCategories: [],
      reasonCodes: [],
      reasonTypes: [],
      selectedReasonCode: null,
      selectedReasonType: null,
      commentRequired: false,
      comment: '',
      requireCodes: false,
    };
  }

  async componentDidMount() {
    const { category, shipment } = this.props;
    await this.getCustomReasonCodes(
      shipment.shipper._id,
      shipment.shipment_type,
      category,
    );
  }

  getCustomReasonCodes = async (shipperId, shipment_type, category) => {
    try {
      const codes = await getSortedReasonCodes(
        shipperId,
        shipment_type,
        category,
      );
      if (!codes || !codes.subCategories || !codes.subCategories.length) {
        return null;
      }

      const { subCategories, requireCodes } = codes;
      const reasonTypes = subCategories.map((s) => ({
        name: s.reasonType,
        value: s.reasonType,
      }));
      this.setState({ reasonTypes, subCategories, requireCodes });

      if (this.props.setCodesRequired) {
        this.props.setCodesRequired(requireCodes);
      }
    } catch (e) {
      console.log('Error fetching custom reason codes', e);
    }
  };

  handleCommentInput = (e) => {
    const comment = e?.target?.value ?? e;
    this.setState({ comment });
    this.props.updateComment(comment);
  };

  renderReasonCodeComment() {
    const { comment } = this.state;
    const { t } = this.props;

    return (
      <div className="w-2/3 mt-4 space-y-1">
        <div className="flex items-center">
          <Label htmlFor="reason-code-comment">
            {t('REASON_CODE_COMMENT')}
          </Label>
          <InfoTooltip>{t('REASON_CODE_REQUIRED_COMMENT')}</InfoTooltip>
        </div>
        <Input
          id="reason-code-comment"
          data-testid="CargomaticInput-29f61462"
          type="text"
          value={comment}
          onChange={this.handleCommentInput}
        />
      </div>
    );
  }

  handleReasonTypeSelect = (value) => {
    const { subCategories } = this.state;
    const subCategory = subCategories.find((s) => s.reasonType === value);

    this.setState({
      selectedReasonType: value,
      commentRequired: subCategory?.commentRequired,
      reasonCodes: subCategory?.reasonCodes || [],
    });
    this.props.updateCommentRequired(subCategory?.commentRequired);
  };

  getReasonCodeValue = (item) => {
    if (!item) return '';
    if (this.props.useDescriptionAsValue) return item.description || '';
    return item.code || item.description || '';
  };

  handleReasonCodeSelect = (value) => {
    const { reasonCodes, selectedReasonType } = this.state;
    const item = reasonCodes.find((c) => this.getReasonCodeValue(c) === value);
    const selectedReasonCode =
      item && item.code !== '' ? item.code : item?.description;
    this.setState({ selectedReasonCode });
    const selectedItemWithType = _.assign(item || {}, {
      reasonType: selectedReasonType,
    });
    this.props.selectReasonCode(selectedItemWithType);
  };

  render() {
    const {
      reasonTypes,
      reasonCodes,
      selectedReasonCode,
      selectedReasonType,
      commentRequired,
      subCategories,
      requireCodes,
    } = this.state;
    const { t } = this.props;
    if (!subCategories || !reasonTypes || !reasonCodes) {
      return <span>{t('NO_REASON_CODES_FOUND')}</span>;
    }

    const reasonTypesLocalized = addlocalizationSlugKey(reasonTypes, 'name');
    const reasonCodesLocalized = addlocalizationSlugKey(
      reasonCodes,
      'description',
    );

    return (
      <div className="relative w-full">
        {requireCodes && (
          <div className="flex flex-row">
            <span className="mt-2 mb-2 italic">
              {t('REASON_CODE_REQUIRED')}
            </span>
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <div className="flex items-center">
              <Label htmlFor="reason-type">{t('REASON_TYPE')}</Label>
              <InfoTooltip>{t('REASON_TYPE_INFO')}</InfoTooltip>
            </div>
            <Select
              value={selectedReasonType || ''}
              onValueChange={this.handleReasonTypeSelect}
            >
              <SelectTrigger
                id="reason-type"
                data-testid="CargomaticSelect-791dd690"
              >
                <SelectValue placeholder={t('REASON_TYPE_CAPTION')} />
              </SelectTrigger>
              <SelectContent>
                {reasonTypesLocalized
                  .filter((item) => item.value)
                  .map((item, index) => (
                    <SelectItem key={index} value={item.value}>
                      {t(item.localizationSlug?.toUpperCase())}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <div className="flex items-center">
              <Label htmlFor="reason-code">{t('REASON_CODE')}</Label>
              <InfoTooltip>{t('SELECT_REASON_CODE')}</InfoTooltip>
            </div>
            <Select
              value={selectedReasonCode || ''}
              onValueChange={this.handleReasonCodeSelect}
              disabled={!selectedReasonType}
            >
              <SelectTrigger
                id="reason-code"
                data-testid="CargomaticSelect-f7cb1813"
                disabled={!selectedReasonType}
              >
                <SelectValue placeholder={t('SELECT_REASON_CODE')} />
              </SelectTrigger>
              <SelectContent>
                {reasonCodesLocalized.map((item, index) => {
                  const optionValue = this.getReasonCodeValue(item);
                  if (!optionValue) return null;
                  return (
                    <SelectItem key={index} value={optionValue}>
                      {t(item.localizationSlug?.toUpperCase())}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>
        {commentRequired && this.renderReasonCodeComment()}
      </div>
    );
  }
}

export default withTranslation()(SelectReasonCodes);
